import { memo, useCallback, useState, type MouseEvent } from 'react'
import { Handle, Position, type NodeProps, useReactFlow } from '@xyflow/react'
import { Button } from '@/ui/button'
import { MaterialIcon } from '@/ui/material-icon'
import type {
  PipelineNode,
  TransformationNode as TransformationNodeType,
} from '../../types/canvas.types'
import styles from './TransformationNode.module.scss'
import { areNodePropsEqual } from '../nodeMemo.util'
import { useAppDispatch } from '@/app/reduxHooks'
import { openOverlay } from '@/features/adp/state/overlaySlice'
import { requiresUpstreamSource } from '../../canvas.utils'
import { cn } from '@/shared/lib/utils'
import { TruncatedSecondaryTitle } from '../TruncatedSecondaryTitle'
import NodeValidationBadge from '../../validation/NodeValidationBadge'
import { useAdpSessionStore } from '@/features/adp/hooks/useAdpSessionStore'
import { store } from '@/app/store'
import { closeOverlayAsync } from '@/features/adp/state/overlay.thunks'

const TransformationNode = memo(({ id, data }: NodeProps<TransformationNodeType>) => {
  const [expanded, setExpanded] = useState(true)
  const { setNodes, setEdges, getNodes, getEdges } = useReactFlow()
  const locked = Boolean(data.locked)
  const transformatioName = data.nodeTitle
  const dispatch = useAppDispatch()
  const { removeNode } = useAdpSessionStore()

  const handleSettings = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()

      if (
        !requiresUpstreamSource(id, getNodes() as PipelineNode[], getEdges(), transformatioName)
      ) {
        return
      }

      if (data.originalId == null) {
        return
      }

      dispatch(
        openOverlay({
          type: 'transformation-sheet',
          data: {
            nodeId: id,
            transformationId: data.originalId,
          },
        })
      )
    },
    [data.originalId, dispatch, getEdges, getNodes, id, transformatioName]
  )

  const handleToggleExpand = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setExpanded(v => !v)
  }, [])

  const handleLock = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      setNodes(nodes =>
        nodes.map(node => {
          if (node.id !== id) return node
          const nodeData = node.data as TransformationNodeType['data']
          const nextLocked = !nodeData?.locked

          return {
            ...node,
            draggable: !nextLocked,
            data: { ...node.data, locked: nextLocked },
          }
        })
      )
    },
    [id, setNodes]
  )

  const handleDuplicate = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      setNodes(nodes => {
        const self = nodes.find(node => node.id === id)
        if (!self) return nodes
        const clone = {
          ...self,
          id: `${self.id}-copy-${Date.now()}`,
          position: { x: self.position.x + 28, y: self.position.y + 28 },
          draggable: true,
          data: { ...self.data, locked: false },
        }
        return nodes.concat(clone)
      })
    },
    [id, setNodes]
  )

  const handleDelete = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      const overlay = store.getState().overlay

      const overlayNodeId =
        overlay.data && typeof overlay.data === 'object' && 'nodeId' in overlay.data
          ? overlay.data.nodeId
          : null

      if (overlay.isOpen && overlayNodeId != null && String(overlayNodeId) === String(id)) {
        dispatch(closeOverlayAsync())
      }

      setNodes(nodes => nodes.filter(node => node.id !== id))
      setEdges(edges => edges.filter(edge => edge.source !== id && edge.target !== id))
      removeNode(id)
    },
    [dispatch, id, removeNode, setEdges, setNodes]
  )

  const titlesBlock = (
    <div className={styles.titles}>
      <div className={styles.title}>{data.nodeTitle}</div>
      {data.nodeSecondaryTitle && (
        <TruncatedSecondaryTitle text={data.nodeSecondaryTitle} className={styles.secondaryTitle} />
      )}
    </div>
  )

  return (
    <div className={styles.node} data-validation={data.validation?.severity}>
      <NodeValidationBadge nodeId={id} validation={data.validation} />
      <Handle
        type="target"
        position={Position.Left}
        className={styles.handle}
        isConnectable={!locked}
      />
      <div className={styles.cardInner}>
        {/* ── Header ── */}
        <div className={cn(styles.header, expanded ? '' : styles.headerCollapsed)}>
          <div className={styles.headerLeft}>
            <span className={styles.headerTitle}>Transformation</span>
            {expanded && (
              <Button
                variant="ghost"
                size="icon"
                className={`${styles.headerIconBtn} nodrag`}
                aria-label="Settings"
                onPointerDown={e => e.stopPropagation()}
                onClick={handleSettings}
              >
                <MaterialIcon name="settings" size={16} />
              </Button>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className={`${styles.headerIconBtn} nodrag`}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            onPointerDown={e => e.stopPropagation()}
            onClick={handleToggleExpand}
          >
            <MaterialIcon name="unfold_more" size={18} />
          </Button>
        </div>

        {expanded ? (
          <>
            {/* ── Body ── */}
            <div className={styles.body}>
              {data.image && (
                <div className={styles.imageWrapper}>
                  <img src={data.image} alt={data.nodeTitle} className={styles.image} />
                </div>
              )}
              {titlesBlock}
            </div>

            {/* ── Footer ── */}
            <div className={styles.footer}>
              <Button
                variant="ghost"
                size="icon"
                className={`${styles.footerBtn} nodrag`}
                aria-label={locked ? 'Unlock node' : 'Lock node'}
                aria-pressed={locked}
                onPointerDown={e => e.stopPropagation()}
                onClick={handleLock}
              >
                <MaterialIcon name={locked ? 'lock' : 'lock_open'} size={18} />
              </Button>
              <div className={styles.footerRight}>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`${styles.footerBtn} nodrag`}
                  aria-label="Duplicate"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={handleDuplicate}
                >
                  <MaterialIcon name="content_copy" size={18} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`${styles.footerBtn} ${styles.footerDelete} nodrag`}
                  aria-label="Delete"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={handleDelete}
                >
                  <MaterialIcon name="delete" size={18} />
                </Button>
              </div>
            </div>
          </>
        ) : (
          /* ── Collapsed row ── */
          <div className={styles.collapsedRow}>
            {data.image && (
              <div className={styles.logoWellSm}>
                <img src={data.image} alt={data.nodeTitle} className={styles.logoImgSm} />
              </div>
            )}
            {titlesBlock}
          </div>
        )}
      </div>
      <Handle
        type="source"
        position={Position.Right}
        className={styles.handle}
        isConnectable={!locked}
      />
    </div>
  )
}, areNodePropsEqual)

TransformationNode.displayName = 'TransformationNode'

export { TransformationNode }
